// Error tracking of the web app and the desktop renderer (Sentry). GET /config/public › monitoring says whether it is
// on; only then is @sentry/browser loaded (a chunk of its own, never part of the first paint), and its reports go
// through the API's tunnel (POST /api/v1/monitoring/errors): the same origin as every API call, so no CSP change, no
// ad-blocker drop and no user IP at Sentry. Off in development. Scrubbed by @moods/shared › scrubEvent. Errors that
// happen before Sentry is ready (boot, first render) wait in a short queue and are sent once it is.
import type * as SentryBrowserModule from '@sentry/browser';
import { scrubBreadcrumb, scrubEvent } from '@moods/shared';
import { API_URL, IS_DESKTOP, IS_IOS_APP } from './env';

type SentryBrowser = Pick<typeof SentryBrowserModule, 'init' | 'captureException'>;
interface PublicMonitoring {
  dsn: string;
  environment: string;
}

const EARLY_LIMIT = 10;
/** benign browser noise that says nothing about MOODS */
const IGNORE_ERRORS = [/ResizeObserver loop/, /^AbortError/, /The operation was aborted/, /Non-Error promise rejection captured/];
const DENY_URLS = [/^chrome-extension:\/\//, /^moz-extension:\/\//, /^safari-(web-)?extension:\/\//];

let sentry: SentryBrowser | null = null;
let off = false;
const early: unknown[] = [];

function remember(err: unknown) {
  if (!off && !sentry && early.length < EARLY_LIMIT) early.push(err);
}
const onError = (e: ErrorEvent) => remember(e.error ?? e.message);
const onRejection = (e: PromiseRejectionEvent) => remember(e.reason);

/** Reports an unexpected error (route error page, caught failures); a no-op while error tracking is off. */
export function reportError(err: unknown): void {
  if (sentry) sentry.captureException(err);
  else remember(err);
}

/** From the first line of main.tsx on: keeps uncaught errors until Sentry takes over. */
export function catchEarlyErrors(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
}

async function publicMonitoring(): Promise<PublicMonitoring | null> {
  try {
    const res = await fetch(`${API_URL}/api/v1/config/public`, { credentials: 'include' });
    if (!res.ok) return null;
    const m = ((await res.json()) as { monitoring?: PublicMonitoring | null }).monitoring;
    return m?.dsn ? m : null;
  } catch {
    return null;
  }
}

export interface InstallOptions {
  enabled?: boolean;
  config?: () => Promise<PublicMonitoring | null>;
  load?: () => Promise<SentryBrowser>;
}

/** Turns error tracking on when the server has a web DSN; true when reports are sent. */
export async function installMonitoring({
  enabled = !import.meta.env.DEV,
  config = publicMonitoring,
  load = () => import('@sentry/browser'),
}: InstallOptions = {}): Promise<boolean> {
  const m = enabled ? await config() : null;
  const S = m ? await load().catch(() => null) : null;
  if (typeof window !== 'undefined') {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
  }
  if (!m || !S) {
    off = true;
    early.length = 0;
    return false;
  }
  S.init({
    dsn: m.dsn,
    tunnel: `${API_URL}/api/v1/monitoring/errors`,
    environment: m.environment,
    release: import.meta.env.VITE_BUILD_SHA,
    sendDefaultPii: false,
    // no release-health sessions: every page load would be a request; errors only
    integrations: defaults => defaults.filter(i => i.name !== 'BrowserSession'),
    initialScope: { tags: { platform: IS_DESKTOP ? 'desktop' : IS_IOS_APP ? 'ios' : 'web' } },
    ignoreErrors: IGNORE_ERRORS,
    denyUrls: DENY_URLS,
    beforeSend: event => scrubEvent(event),
    beforeBreadcrumb: crumb => scrubBreadcrumb(crumb),
  });
  sentry = S;
  for (const err of early.splice(0)) S.captureException(err);
  return true;
}
